import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format, isValid } from 'date-fns'; // 1. Added isValid
import { useTranslation } from 'react-i18next';

import MoodDepressedIcon from '../../assets/icons/mood-depressed.svg';
import MoodSadIcon from '../../assets/icons/mood-sad.svg';
import MoodOkayIcon from '../../assets/icons/mood-okay.svg';
import MoodHappyIcon from '../../assets/icons/mood-happy.svg';

import { BorderlessButton } from '../buttons';
import { Edit, ArrowDown, ArrowUp } from '../icons';
import Modal from '../Modal';
import DiaryEntryEditContainer from '../../containers/DiaryEntryEditContainer';

const DiaryEntryView = ({ entry }) => {
  const { t } = useTranslation();
  const [showHiddenContent, setShowHiddenContent] = useState(false);
  const [editDiaryEntryOpen, setEditDiaryEntryOpen] = useState(false);

  const feelings = {
    depressed: MoodDepressedIcon,
    sad: MoodSadIcon,
    okay: MoodOkayIcon,
    happy: MoodHappyIcon
  };

  /**
   * 2. DEFENSIVE DATE HELPERS
   * These prevent RangeErrors if createdAt is missing or malformed.
   */
  const formatDate = (date) => {
    const d = new Date(date);
    return isValid(d) ? format(d, 'MMM do, yyyy') : t('diaryEntryView.dateUnknown');
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return isValid(d) ? format(d, 'h:mm a') : '--:--';
  };

  const openEditModal = () => setEditDiaryEntryOpen(true);
  const closeEditModal = () => setEditDiaryEntryOpen(false);
  const toggleVisibility = () => setShowHiddenContent(!showHiddenContent);

  const displayContent = !entry.hidden || showHiddenContent;

  return (
    <div className="diary-entry">
      <Modal isOpen={editDiaryEntryOpen} onRequestClose={closeEditModal}>
        <DiaryEntryEditContainer entry={entry} onDiaryEntrySaved={closeEditModal} />
      </Modal>

      <div className="diary-entry__header">
        <div className="diary-entry__timestamp">
          <p className="diary-entry__date">{formatDate(entry.createdAt)}</p>
          <p className="diary-entry__time">
            {t('diaryEntryView.postedAt', {
              time: formatTime(entry.createdAt),
            })}
          </p>
        </div>

        <div className="diary-entry__feeling-container">
          <div className={`diary-entry__feeling diary-entry__feeling--${entry.feeling}`}>
            {t('diaryEntryView.feeling')}
            <img
              src={feelings[entry.feeling]}
              alt={t(`diary.${entry.feeling}`)}
              className="diary-entry__feeling-icon"
            />
          </div>
        </div>
      </div>

      {displayContent && <div className="diary-entry__content">{entry.content}</div>}

      <div className="diary-entry__footer">
        {entry.hidden && (
          <div className={`diary-entry__footer-${showHiddenContent ? 'hide' : 'show'}`}>
            {t('diaryEntryView.hidden')}{' '}
            <BorderlessButton
              onClick={toggleVisibility}
              label={showHiddenContent ? t('diaryEntryView.hideEntry') : t('diaryEntryView.showEntry')}
              IconRight={showHiddenContent ? ArrowUp : ArrowDown}
              colorScheme="blue"
            />
          </div>
        )}

        <div className="diary-entry__footer-edit">
          <BorderlessButton
            onClick={openEditModal}
            label={t('diaryEntryView.editEntry')}
            IconLeft={Edit}
            colorScheme="blue"
          />
        </div>
      </div>
    </div>
  );
};

DiaryEntryView.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.string,
    feeling: PropTypes.string,
    hidden: PropTypes.bool,
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

export default DiaryEntryView;
